import React, { useState, useEffect, useRef } from 'react';
import { generatePuzzle, LEVELS } from '../utils/puzzleGenerator';
import {
  isMoveCorrect,
  emptyNotes,
  toggleNote,
  getUTCDateString,
  hashStringToSeed,
  mulberry32,
  formatTimer,
  countDigit,
} from '../utils/gameLogic';
import confetti from 'canvas-confetti';
import Board from './Board';
import { primaryButton, secondaryButton, toggleActiveButton, toggleInactiveButton } from '../utils/buttonStyles';
import { RefreshCw, Pencil, Undo2, Lightbulb, X } from 'lucide-react';

const FREEPLAY_SAVE_KEY = 'sudokuFreeplaySave';
const DAILY_LEVEL = 'hard';

const LEVEL_LABELS = {
  easy: '◆ Easy',
  medium: '◆ Medium',
  hard: '◆ Hard',
};

const Game = ({ points, setPoints, streak, setStreak, mode = 'freeplay', onWin }) => {
  const [level, setLevel] = useState(mode === 'daily' ? DAILY_LEVEL : 'medium');
  const saveKey = mode === 'daily' ? `sudokuDailySave_${getUTCDateString()}` : FREEPLAY_SAVE_KEY;
  const [puzzle, setPuzzle] = useState(null);
  const [solution, setSolution] = useState(null);
  const [userGrid, setUserGrid] = useState(Array(9).fill().map(() => Array(9).fill(0)));
  const [selectedCell, setSelectedCell] = useState({ row: -1, col: -1 });
  const [filterDigit, setFilterDigit] = useState(null);
  const [timer, setTimer] = useState(0);
  const [status, setStatus] = useState('playing'); // 'playing' | 'won'
  const [mistakes, setMistakes] = useState(0);
  const [flashCell, setFlashCell] = useState(null); // { row, col } briefly highlighted red
  const [notes, setNotes] = useState(emptyNotes());
  const [notesMode, setNotesMode] = useState(false);
  const [history, setHistory] = useState([]); // [{ row, col, prevValue, prevNotes }]
  const [hintsUsed, setHintsUsed] = useState(0);
  const [modalDismissed, setModalDismissed] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const saved = loadSavedGame();
    if (saved) {
      setLevel(saved.level);
      setPuzzle(saved.puzzle);
      setSolution(saved.solution);
      setUserGrid(saved.userGrid);
      setNotes(saved.notes);
      setTimer(saved.timer);
      setMistakes(saved.mistakes);
      setHintsUsed(saved.hintsUsed);
      const normalizedStatus = saved.status === 'won' ? 'won' : 'playing';
      setStatus(normalizedStatus);
      setSelectedCell({ row: -1, col: -1 });
      setFilterDigit(null);
      if (normalizedStatus === 'playing') {
        timerRef.current = setInterval(() => setTimer(t => t + 1), 1000);
      }
    } else {
      newGame();
    }
    return () => clearInterval(timerRef.current);
  }, []);

  function loadSavedGame() {
    try {
      const raw = localStorage.getItem(saveKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  useEffect(() => {
    if (!puzzle) return;
    localStorage.setItem(saveKey, JSON.stringify({
      level, puzzle, solution, userGrid, notes, timer, mistakes, hintsUsed, status,
    }));
  }, [level, puzzle, solution, userGrid, notes, timer, mistakes, hintsUsed, status]);

  const newGame = () => {
    const rng = mode === 'daily' ? mulberry32(hashStringToSeed(getUTCDateString())) : Math.random;
    const { puzzle: newPuzzle, solution: newSolution } = generatePuzzle(LEVELS[level], rng);
    setPuzzle(newPuzzle);
    setSolution(newSolution);
    setUserGrid(newPuzzle.map(row => [...row]));
    setSelectedCell({ row: -1, col: -1 });
    setFilterDigit(null);
    setTimer(0);
    setStatus('playing');
    setModalDismissed(false);
    setMistakes(0);
    setFlashCell(null);
    setNotes(emptyNotes());
    setHistory([]);
    setHintsUsed(0);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setTimer(t => t + 1), 1000);
  };

  const handleCellClick = (row, col) => {
    if (status !== 'playing') return;
    setSelectedCell({ row, col });
    setFilterDigit(null);
  };

  const handleNumberInput = (num) => {
    if (selectedCell.row === -1 || status !== 'playing') return;
    const { row, col } = selectedCell;
    if (puzzle[row][col] !== 0) return;

    setHistory(prev => [...prev, {
      row, col,
      prevValue: userGrid[row][col],
      prevNotes: notes[row][col],
    }]);

    if (notesMode) {
      setNotes(prev => toggleNote(prev, row, col, num));
      return;
    }

    const newGrid = userGrid.map(r => [...r]);
    newGrid[row][col] = num;
    setUserGrid(newGrid);
    setNotes(prev => {
      const next = prev.map(r => r.map(cell => [...cell]));
      next[row][col] = [];
      return next;
    });

    if (!isMoveCorrect(solution, row, col, num)) {
      setFlashCell({ row, col });
      setTimeout(() => setFlashCell(null), 400);
      setMistakes(m => m + 1);
      return;
    }

    // Check win
    if (JSON.stringify(newGrid) === JSON.stringify(solution)) {
      clearInterval(timerRef.current);
      setStatus('won');
      confetti({ particleCount: 200, spread: 90, origin: { y: 0.6 } });
      setStreak(s => s + 1);
      // Post to leaderboard stub
      console.log('Post score:', { time: timer, level, user: 'You' });
      if (mode === 'daily' && onWin) onWin(timer, mistakes);
    }
  };

  const handlePadClick = (num) => {
    if (status !== 'playing') return;
    const { row, col } = selectedCell;
    const hasSelection = row !== -1 && col !== -1;
    const isEditable = hasSelection && puzzle[row][col] === 0;

    if (isEditable) {
      handleNumberInput(num);
      return;
    }

    // No selection or given selected → filter highlight (toggle off if same digit)
    setFilterDigit(prev => (prev === num ? null : num));
  };

  const handleUndo = () => {
    if (history.length === 0 || status !== 'playing') return;
    const last = history[history.length - 1];
    setHistory(prev => prev.slice(0, -1));
    setUserGrid(prev => {
      const next = prev.map(r => [...r]);
      next[last.row][last.col] = last.prevValue;
      return next;
    });
    setNotes(prev => {
      const next = prev.map(r => r.map(cell => [...cell]));
      next[last.row][last.col] = last.prevNotes;
      return next;
    });
    setSelectedCell({ row: last.row, col: last.col });
    setFilterDigit(null);
  };

  const getHint = () => {
    if (points < 1) return; // Costs 1 point
    setPoints(p => p - 1);
    setHintsUsed(h => h + 1);
    const { row, col } = selectedCell;
    if (row !== -1 && puzzle[row][col] === 0) {
      const newGrid = userGrid.map(r => [...r]);
      newGrid[row][col] = solution[row][col];
      setUserGrid(newGrid);
    }
  };

  const moveSelection = (dRow, dCol) => {
    setFilterDigit(null);
    setSelectedCell(prev => {
      const row = prev.row === -1 ? 0 : Math.min(8, Math.max(0, prev.row + dRow));
      const col = prev.col === -1 ? 0 : Math.min(8, Math.max(0, prev.col + dCol));
      return { row, col };
    });
  };

  const clearSelectedCell = () => {
    if (selectedCell.row === -1 || status !== 'playing') return;
    const { row, col } = selectedCell;
    if (puzzle[row][col] !== 0) return;
    setHistory(prev => [...prev, {
      row, col,
      prevValue: userGrid[row][col],
      prevNotes: notes[row][col],
    }]);
    setUserGrid(prev => {
      const next = prev.map(r => [...r]);
      next[row][col] = 0;
      return next;
    });
    setNotes(prev => {
      const next = prev.map(r => r.map(cell => [...cell]));
      next[row][col] = [];
      return next;
    });
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (status !== 'playing') return;
      const active = document.activeElement;
      if (active && (active.tagName === 'INPUT' || active.tagName === 'SELECT')) return;

      if (e.key >= '1' && e.key <= '9') {
        e.preventDefault();
        handlePadClick(Number(e.key));
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        clearSelectedCell();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        moveSelection(-1, 0);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        moveSelection(1, 0);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        moveSelection(0, -1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        moveSelection(0, 1);
      } else if (e.key === 'Escape') {
        setSelectedCell({ row: -1, col: -1 });
        setFilterDigit(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [status, selectedCell, userGrid, notes, notesMode, solution, mistakes, mode, onWin, timer, puzzle, level, filterDigit]);

  if (!puzzle) return <div className="text-center text-gray-600 dark:text-gray-300">Loading puzzle...</div>;

  return (
    <div className="max-w-md mx-auto">
      {mode === 'freeplay' && (
        <div className="mb-4 flex flex-wrap items-center justify-center gap-2">
          <select
            value={level}
            onChange={e => setLevel(e.target.value)}
            className="p-2 border rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
          >
            {Object.keys(LEVELS).map(l => (
              <option key={l} value={l}>{LEVEL_LABELS[l] || l}</option>
            ))}
          </select>
          <button onClick={newGame} className={primaryButton}>
            <RefreshCw size={16} /> New Game
          </button>
          <button
            onClick={() => setNotesMode(m => !m)}
            className={notesMode ? toggleActiveButton : toggleInactiveButton}
          >
            <Pencil size={16} /> Notes {notesMode ? 'On' : 'Off'}
          </button>
          <button
            onClick={handleUndo}
            disabled={history.length === 0}
            className={secondaryButton}
          >
            <Undo2 size={16} /> Undo
          </button>
        </div>
      )}
      {mode === 'daily' && (
        <div className="mb-4 flex justify-center gap-2">
          <button
            onClick={() => setNotesMode(m => !m)}
            className={notesMode ? toggleActiveButton : toggleInactiveButton}
          >
            <Pencil size={16} /> Notes {notesMode ? 'On' : 'Off'}
          </button>
          <button
            onClick={handleUndo}
            disabled={history.length === 0}
            className={secondaryButton}
          >
            <Undo2 size={16} /> Undo
          </button>
        </div>
      )}
      <Board
        puzzle={puzzle}
        userGrid={userGrid}
        notes={notes}
        selectedCell={selectedCell}
        flashCell={flashCell}
        filterDigit={filterDigit}
        onCellClick={handleCellClick}
      />
      <p className="mt-4 text-sm text-gray-600 dark:text-gray-400 text-center">
        Play free Sudoku in your browser with Easy, Medium, and Hard puzzles, pencil notes, undo,
        and a shared Daily Challenge with streaks. Wrong entries flash and count as mistakes—there
        is no timer that ends the game—so you can keep solving until the board is complete. Read{' '}
        <a
          href="/learn"
          className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 underline-offset-2 hover:underline"
        >
          Learn How to Play
        </a>{' '}
        for the rules, or{' '}
        <a
          href="/privacy/"
          className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 underline-offset-2 hover:underline"
        >
          Privacy
        </a>{' '}
        for how we handle analytics and ads.
      </p>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => {
          const remaining = Math.max(0, 9 - countDigit(userGrid, num));
          const isComplete = remaining === 0;
          const isFilterActive = filterDigit === num;
          return (
            <button
              key={num}
              type="button"
              onClick={() => handlePadClick(num)}
              aria-label={`Number ${num}, ${remaining} remaining`}
              className={`relative w-10 h-10 rounded-lg font-semibold transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 ${
                isComplete
                  ? 'bg-gray-200 text-gray-400 dark:bg-gray-800 dark:text-gray-500 opacity-60'
                  : isFilterActive
                  ? 'bg-indigo-300 text-indigo-900 dark:bg-indigo-700 dark:text-indigo-100'
                  : 'bg-indigo-100 hover:bg-indigo-200 active:bg-indigo-300 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-100 dark:hover:bg-indigo-800'
              }`}
            >
              {num}
              <span className="absolute -top-1 -right-1 min-w-[1rem] h-4 px-0.5 rounded-full bg-white dark:bg-gray-900 text-[10px] leading-4 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                {remaining}
              </span>
            </button>
          );
        })}
      </div>
      <div className="mt-4 text-center text-gray-700 dark:text-gray-200">
        <p>Time: {formatTimer(timer)} | Hints: {hintsUsed} | Mistakes: {mistakes}</p>
        <button onClick={getHint} disabled={points < 1} className={`${primaryButton} mt-2`}>
          <Lightbulb size={16} /> Hint (1 pt)
        </button>
      </div>
      {status === 'won' && !modalDismissed && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-fade-in">
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 text-center max-w-sm mx-4 animate-pop-in">
            <button
              onClick={() => setModalDismissed(true)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <h2 className="text-2xl font-bold text-green-600 dark:text-green-400 mb-4">You Win!</h2>
            <p className="text-gray-700 dark:text-gray-200 mb-1">Time: {formatTimer(timer)}</p>
            <p className="text-gray-700 dark:text-gray-200 mb-1">Mistakes: {mistakes}</p>
            <p className="text-gray-700 dark:text-gray-200 mb-4">Hints used: {hintsUsed}</p>
            {mode === 'freeplay' && (
              <button onClick={newGame} className={primaryButton}>New Game</button>
            )}
          </div>
        </div>
      )}
      {/* Mobile vibrate on win */}
      {status === 'won' && 'vibrate' in navigator && navigator.vibrate(200)}
    </div>
  );
};

export default Game;
